import { DateTime, Interval } from "luxon";
import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  computeProximityPercent,
  generateSquareCharacters,
} from "../domain/geography";
import { Guess } from "../domain/guess";
import React from "react";
import { SettingsData } from "../hooks/useSettings";

const START_DATE = DateTime.fromISO("2023-12-15");

interface ShareProps {
  guesses: Guess[];
  dayString: string;
  settingsData: SettingsData;
  hideImageMode: boolean;
  rotationMode: boolean;
}

export function Share({
  guesses,
  dayString,
  settingsData,
  hideImageMode,
  rotationMode,
}: ShareProps) {
  const { t } = useTranslation();
  const { theme } = settingsData;

  const shareText = useMemo(() => {
    const guessCount =
      guesses[guesses.length - 1]?.distance === 0 ? guesses.length : "X";
    const dayCount = Math.floor(
      Interval.fromDateTimes(START_DATE, new Date(dayString)).length("day")
    );
    const difficultyModifierEmoji = hideImageMode
      ? " 🙈"
      : rotationMode
      ? " 🌀"
      : "";
    const title = `#Versedle #${dayCount} ${guessCount}/6${difficultyModifierEmoji}`;

    const guessString = guesses
      .map((guess) => {
        const percent = computeProximityPercent(guess.distance);
        return generateSquareCharacters(percent, theme).join("");
      })
      .join("\n");

    return [title, guessString, "", "https://www.versedle.com/"].join("\n");
  }, [dayString, guesses, hideImageMode, rotationMode, theme]);

  const guessSquares = useMemo(() => {
    return guesses
      .map((guess) => {
        const percent = computeProximityPercent(guess.distance);
        return generateSquareCharacters(percent, theme).join("");
      })
      .join("\n");
  }, [guesses, theme]);

  const showCopiedToast = useCallback(() => {
    toast(
      <div style={{ textAlign: "center" }}>
        <div style={{ fontWeight: "600", marginBottom: "8px" }}>
          Score copied to clipboard!
        </div>
        <div
          style={{
            fontSize: "20px",
            lineHeight: "1.2",
            whiteSpace: "pre-line",
          }}
        >
          {guessSquares}
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeButton: false,
        style: {
          background: "#fefef8",
          color: "#333",
          border: "1px solid black",
          borderRadius: "8px",
          fontSize: "14px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        },
      }
    );
  }, [guessSquares]);

  const handleShare = useCallback(async () => {
    // Check if we're on mobile (touch device with small screen)
    const isMobile = "ontouchstart" in window && window.innerWidth <= 768;

    // Use Web Share API only on mobile
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          text: shareText,
        });
      } catch (err: any) {
        // User cancelled or share failed, fall back to clipboard
        if (err.name !== "AbortError") {
          await navigator.clipboard.writeText(shareText);
          showCopiedToast();
        }
      }
    } else {
      // Use clipboard for desktop
      await navigator.clipboard.writeText(shareText);
      showCopiedToast();
    }
  }, [shareText, showCopiedToast]);

  return (
    <button
      onClick={handleShare}
      style={{
        width: "100%",
        padding: "12px 20px",
        marginTop: "10px",
        background: "black",
        border: "1px solid black",
        borderRadius: "8px",
        color: "white",
        fontSize: "16px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#333";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "black";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        Share your results
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          height="20"
          viewBox="0 0 24 24"
          width="20"
        >
          <path
            fill="white"
            d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92zM18 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM6 13c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm12 7.02c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"
          ></path>
        </svg>
      </span>
    </button>
  );
}
